/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { ErrorMessage } from '../../common';
import { capitalizeFirstLetter, getErrorMessages } from '../../../utils';
import { TAgency, TFreelancerData } from '../../../types';
import LoadingSpinner from '../../common/LoadingSpinner';
import { executeHttpRequest } from '../../../epics/api';
import { TRootState } from '../../../reducers';


type TClaimedData = {
    id: number;
    message: string;
    status: 'success' | 'warning' | 'error';
}

type TClaimFreelancersPromptProps = {
    freelancers: TFreelancerData[];
    closeModal: () => void;
    setClaimedData: (data: TClaimedData[]) => void;
}

function ClaimFreelancersPrompt({
    freelancers,
    closeModal,
    setClaimedData,
}: TClaimFreelancersPromptProps) {
    const user = useSelector((state: TRootState) => state.user);

    const [agencyOptions, setAgencyOptions] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (user.isAgent && user.isSuperUser) {
            executeHttpRequest('agencies/', 'GET')
                .then(response => {
                    setAgencyOptions(response.data.content.map((agency: TAgency) => ({
                        label: agency.company_name,
                        value: agency.id,
                    })));
                }).catch(err => {
                    setError(`Could not fetch agencies. Error: ${getErrorMessages(err)}`);
                });
        }
    }, []);

    const renderFreelancersIdsList = () => freelancers.map((fr, i) =>
        <span key={fr.id}>
            {!!i && ', '}<strong>{fr.id}</strong>
        </span>
    );

    const handleClaimFreelancers = () => {
        setIsLoading(true);

        const payload = {
            ...(selectedAgency && { agency: selectedAgency.value }),
            freelancers: freelancers.map(freelancer => freelancer.id),
        };

        executeHttpRequest(
            'unallocated-freelancers/claim/',
            'POST',
            payload,
        ).then(response => {
            setIsLoading(false);
            setClaimedData(response.data.content);
        }).catch(err => {
            setIsLoading(false);
            setError(getErrorMessages(err));
        });
    };

    return (
        <>
            <p className="modal-title">Claim workers</p>
            <div className="unallocated-freelancers-modal-body">
                <p>
                    Are you sure you want to claim worker{freelancers.length > 1 ? 's' : ''}:{' '}
                    {renderFreelancersIdsList()}
                    ?
                </p>
                {user.isAgent && user.isSuperUser &&
                    <>
                        <p className="unallocated-freelancers-admin-prompt">
                            Please select a destination agency:
                        </p>
                        <Select
                            name="claim-agency"
                            value={selectedAgency}
                            onChange={event => setSelectedAgency(event)}
                            options={agencyOptions}
                            isDisabled={isLoading}
                            isClearable={true}
                            isSearchable={true}
                        />
                    </>
                }
            </div>
            <div className="buttons-container">
                <button
                    className="btn btn--round-small"
                    type="button"
                    disabled={isLoading}
                    onClick={handleClaimFreelancers}
                >
                    {isLoading ? (
                        <LoadingSpinner
                            className="mass-booking-spinner"
                            backgroundColor="white"
                        />
                    ) : (
                        'CONFIRM'
                    )}
                </button>
                <button
                    className="btn cancel-btn"
                    type="button"
                    onClick={closeModal}
                >
                    CANCEL
                </button>
            </div>
            {error && (
                <ErrorMessage message={error}/>
            )}
        </>
    );
}

type TClaimFreelancersConfirmationProps = {
    claimedData: TClaimedData[];
    closeModal: () => void;
    onActionSuccess: () => void;
}

function ClaimFreelancersConfirmation({
    claimedData,
    closeModal,
    onActionSuccess,
}: TClaimFreelancersConfirmationProps) {

    const renderResults = () => claimedData?.map(result => {
        const className = `mass-booking-item-result ${result.status}`;
        const message = capitalizeFirstLetter(result.message).replaceAll('_', ' ');
        return (
            <span className={className} key={result.id}>
                Worker {result.id} -&gt; {message}<br/>
            </span>
        );
    });

    const handleOkClick = () => {
        closeModal();
        onActionSuccess();
    };

    return (
        <>
            <p className="modal-title">Claim workers</p>
            <div className="mass-booking-results-container">
                {renderResults()}
            </div>
            <button
                className="btn btn--round-small"
                type="button"
                onClick={handleOkClick}
            >
                OK
            </button>
        </>
    );
}


type TClaimFreelancersContainerProps = {
    freelancers: TFreelancerData[];
    onActionSuccess: () => void;
    closeModal: () => void;
};

export function ClaimFreelancersContainer({
    freelancers,
    onActionSuccess,
    closeModal,
}: TClaimFreelancersContainerProps) {
    const [claimedData, setClaimedData] = useState(null);

    return (
        <div>
            <div className="modal-dialog unallocated-freelancers-modal-container">
                {!claimedData ? (
                    <ClaimFreelancersPrompt
                        freelancers={freelancers}
                        closeModal={closeModal}
                        setClaimedData={setClaimedData}
                    />
                ) : (
                    <ClaimFreelancersConfirmation
                        claimedData={claimedData}
                        closeModal={closeModal}
                        onActionSuccess={onActionSuccess}
                    />
                )}
            </div>
        </div>
    );
}
